import React, { useEffect, useState } from "react";
import { Navigate, Link, useNavigate } from "react-router-dom";
import { api, formatApiErrorDetail } from "../lib/api";
import { toast } from "sonner";
import { Pencil, Trash2, Plus, LogOut, Package, ShoppingCart, Mail, Upload, Image as ImageIcon, Tag, Settings as SettingsIcon, Images, Truck, Percent, Bell } from "lucide-react";
import { resolveImageUrl } from "../lib/images";
import { useCategories, useSettings } from "../context/StoreDataContext";

export default function AdminDashboard() {
    const token = localStorage.getItem("admin_token");
    const [tab, setTab] = useState("products");
    const [products, setProducts] = useState([]);
    const [orders, setOrders] = useState([]);
    const [subscribers, setSubscribers] = useState([]);
    const [slides, setSlides] = useState([]);
    const [zones, setZones] = useState([]);
    const [promotions, setPromotions] = useState([]);
    const [unseenCount, setUnseenCount] = useState(0);
    const [editing, setEditing] = useState(null);
    const { categories, reloadCategories } = useCategories();
    const { settings, reloadSettings } = useSettings();
    const navigate = useNavigate();

    useEffect(() => {
        if (!token) return;
        loadProducts();
        loadOrders();
        loadSubscribers();
        loadSlides();
        loadZones();
        loadPromotions();
        loadUnseen();
        const t = setInterval(loadUnseen, 20000);
        return () => clearInterval(t);
    }, [token]);

    useEffect(() => {
        if (tab === "orders" && unseenCount > 0) {
            // mark all current unseen orders as seen
            (async () => {
                const unseen = orders.filter((o) => !o.seen_by_admin);
                await Promise.all(unseen.map((o) => api.put(`/orders/${o.id}/seen`).catch(() => null)));
                loadOrders();
                loadUnseen();
            })();
        }
    }, [tab]);

    function loadProducts() {
        api.get("/products").then((r) => setProducts(r.data));
    }
    function loadOrders() {
        api.get("/orders")
            .then((r) => setOrders(r.data))
            .catch(() => setOrders([]));
    }
    function loadSubscribers() {
        api.get("/subscribers")
            .then((r) => setSubscribers(r.data))
            .catch(() => setSubscribers([]));
    }
    function loadSlides() {
        api.get("/admin/slides").then((r) => setSlides(r.data)).catch(() => setSlides([]));
    }
    function loadZones() {
        api.get("/shipping-zones").then((r) => setZones(r.data)).catch(() => setZones([]));
    }
    function loadPromotions() {
        api.get("/promotions").then((r) => setPromotions(r.data)).catch(() => setPromotions([]));
    }
    function loadUnseen() {
        api.get("/orders/unseen-count").then((r) => setUnseenCount(r.data.count)).catch(() => setUnseenCount(0));
    }

    if (!token) return <Navigate to="/admin/login" replace />;

    function handleLogout() {
        localStorage.removeItem("admin_token");
        localStorage.removeItem("admin_email");
        navigate("/admin/login");
    }

    async function deleteProduct(id) {
        if (!window.confirm("¿Eliminar este producto?")) return;
        try {
            await api.delete(`/products/${id}`);
            toast.success("Producto eliminado");
            loadProducts();
        } catch (err) {
            toast.error(formatApiErrorDetail(err.response?.data?.detail));
        }
    }

    async function updateOrderStatus(id, status) {
        try {
            await api.put(`/orders/${id}/status`, { status });
            toast.success("Estado actualizado");
            loadOrders();
        } catch (err) {
            toast.error(formatApiErrorDetail(err.response?.data?.detail));
        }
    }

    return (
        <div className="min-h-screen bg-ivory" data-testid="admin-dashboard">
            <header className="bg-white border-b border-border-soft">
                <div className="px-6 lg:px-10 py-4 flex items-center justify-between">
                    <Link to="/" className="flex items-baseline gap-2">
                        <span className="font-serif italic text-2xl text-charcoal">Luz de</span>
                        <span className="font-serif text-2xl text-rose">Angely</span>
                        <span className="ml-3 text-xs uppercase tracking-[0.2em] text-taupe">Admin</span>
                    </Link>
                    <button
                        onClick={handleLogout}
                        className="text-xs tracking-[0.2em] uppercase text-taupe hover:text-rose inline-flex items-center gap-2"
                        data-testid="admin-logout-btn"
                    >
                        <LogOut strokeWidth={1.5} className="w-4 h-4" /> Salir
                    </button>
                </div>
            </header>

            <div className="px-6 lg:px-10 py-8">
                <div className="flex gap-2 mb-8 border-b border-border-soft">
                    <button
                        onClick={() => setTab("products")}
                        className={`px-4 py-3 text-xs tracking-[0.2em] uppercase inline-flex items-center gap-2 border-b-2 ${
                            tab === "products"
                                ? "border-rose text-rose"
                                : "border-transparent text-taupe hover:text-charcoal"
                        }`}
                        data-testid="tab-products"
                    >
                        <Package strokeWidth={1.5} className="w-4 h-4" /> Productos
                        <span className="text-[10px] bg-blush px-2 py-0.5 rounded-full">{products.length}</span>
                    </button>
                    <button
                        onClick={() => setTab("orders")}
                        className={`relative px-4 py-3 text-xs tracking-[0.2em] uppercase inline-flex items-center gap-2 border-b-2 ${
                            tab === "orders"
                                ? "border-rose text-rose"
                                : "border-transparent text-taupe hover:text-charcoal"
                        }`}
                        data-testid="tab-orders"
                    >
                        <ShoppingCart strokeWidth={1.5} className="w-4 h-4" /> Pedidos
                        <span className="text-[10px] bg-blush px-2 py-0.5 rounded-full">{orders.length}</span>
                        {unseenCount > 0 && (
                            <span
                                className="absolute -top-1 -right-1 bg-red-500 text-white text-[10px] font-bold rounded-full min-w-[18px] h-[18px] px-1 flex items-center justify-center animate-pulse"
                                data-testid="orders-unseen-badge"
                            >
                                {unseenCount}
                            </span>
                        )}
                    </button>
                    <button
                        onClick={() => setTab("subscribers")}
                        className={`px-4 py-3 text-xs tracking-[0.2em] uppercase inline-flex items-center gap-2 border-b-2 ${
                            tab === "subscribers"
                                ? "border-rose text-rose"
                                : "border-transparent text-taupe hover:text-charcoal"
                        }`}
                        data-testid="tab-subscribers"
                    >
                        <Mail strokeWidth={1.5} className="w-4 h-4" /> Suscriptores
                        <span className="text-[10px] bg-blush px-2 py-0.5 rounded-full">{subscribers.length}</span>
                    </button>
                    <button
                        onClick={() => setTab("categories")}
                        className={`px-4 py-3 text-xs tracking-[0.2em] uppercase inline-flex items-center gap-2 border-b-2 ${
                            tab === "categories"
                                ? "border-rose text-rose"
                                : "border-transparent text-taupe hover:text-charcoal"
                        }`}
                        data-testid="tab-categories"
                    >
                        <Tag strokeWidth={1.5} className="w-4 h-4" /> Categorías
                        <span className="text-[10px] bg-blush px-2 py-0.5 rounded-full">{categories.length}</span>
                    </button>
                    <button
                        onClick={() => setTab("settings")}
                        className={`px-4 py-3 text-xs tracking-[0.2em] uppercase inline-flex items-center gap-2 border-b-2 ${
                            tab === "settings"
                                ? "border-rose text-rose"
                                : "border-transparent text-taupe hover:text-charcoal"
                        }`}
                        data-testid="tab-settings"
                    >
                        <SettingsIcon strokeWidth={1.5} className="w-4 h-4" /> Configuración
                    </button>
                    <button
                        onClick={() => setTab("slides")}
                        className={`px-4 py-3 text-xs tracking-[0.2em] uppercase inline-flex items-center gap-2 border-b-2 ${
                            tab === "slides"
                                ? "border-rose text-rose"
                                : "border-transparent text-taupe hover:text-charcoal"
                        }`}
                        data-testid="tab-slides"
                    >
                        <Images strokeWidth={1.5} className="w-4 h-4" /> Carrusel
                        <span className="text-[10px] bg-blush px-2 py-0.5 rounded-full">{slides.length}</span>
                    </button>
                    <button
                        onClick={() => setTab("shipping")}
                        className={`px-4 py-3 text-xs tracking-[0.2em] uppercase inline-flex items-center gap-2 border-b-2 ${
                            tab === "shipping"
                                ? "border-rose text-rose"
                                : "border-transparent text-taupe hover:text-charcoal"
                        }`}
                        data-testid="tab-shipping"
                    >
                        <Truck strokeWidth={1.5} className="w-4 h-4" /> Envíos
                        <span className="text-[10px] bg-blush px-2 py-0.5 rounded-full">{zones.length}</span>
                    </button>
                    <button
                        onClick={() => setTab("promotions")}
                        className={`px-4 py-3 text-xs tracking-[0.2em] uppercase inline-flex items-center gap-2 border-b-2 ${
                            tab === "promotions"
                                ? "border-rose text-rose"
                                : "border-transparent text-taupe hover:text-charcoal"
                        }`}
                        data-testid="tab-promotions"
                    >
                        <Percent strokeWidth={1.5} className="w-4 h-4" /> Promociones
                        <span className="text-[10px] bg-blush px-2 py-0.5 rounded-full">{promotions.length}</span>
                    </button>
                </div>

                {tab === "products" && (
                    <div data-testid="products-panel">
                        <div className="flex justify-between items-center mb-6">
                            <h2 className="font-serif text-3xl text-charcoal">Productos</h2>
                            <button
                                onClick={() => setEditing("new")}
                                className="bg-rose text-white hover:bg-rose-dark rounded-md px-5 py-2.5 transition text-xs tracking-[0.2em] uppercase inline-flex items-center gap-2"
                                data-testid="add-product-btn"
                            >
                                <Plus strokeWidth={1.5} className="w-4 h-4" /> Nuevo producto
                            </button>
                        </div>
                        <div className="bg-white rounded-md border border-border-soft overflow-hidden">
                            <table className="w-full text-sm">
                                <thead className="bg-blush text-taupe text-xs uppercase tracking-wider">
                                    <tr>
                                        <th className="text-left px-4 py-3">Imagen</th>
                                        <th className="text-left px-4 py-3">Nombre</th>
                                        <th className="text-left px-4 py-3">Categoría</th>
                                        <th className="text-left px-4 py-3">Precio</th>
                                        <th className="text-left px-4 py-3">Stock</th>
                                        <th className="text-left px-4 py-3">Destacado</th>
                                        <th className="text-right px-4 py-3">Acciones</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {products.map((p) => (
                                        <tr key={p.id} className="border-t border-border-soft" data-testid={`product-row-${p.id}`}>
                                            <td className="px-4 py-3">
                                                <div className="w-12 h-12 rounded bg-blush overflow-hidden">
                                                    {p.images?.[0] && <img src={resolveImageUrl(p.images[0])} alt="" className="w-full h-full object-cover" />}
                                                </div>
                                            </td>
                                            <td className="px-4 py-3 font-serif text-base">{p.name}</td>
                                            <td className="px-4 py-3 capitalize">{p.category}</td>
                                            <td className="px-4 py-3">${p.price.toFixed(2)}</td>
                                            <td className="px-4 py-3">{p.stock}</td>
                                            <td className="px-4 py-3">{p.featured ? "Sí" : "—"}</td>
                                            <td className="px-4 py-3 text-right">
                                                <button
                                                    onClick={() => setEditing(p)}
                                                    className="text-taupe hover:text-rose p-2 inline-block"
                                                    data-testid={`edit-product-${p.id}`}
                                                    aria-label="Editar"
                                                >
                                                    <Pencil strokeWidth={1.5} className="w-4 h-4" />
                                                </button>
                                                <button
                                                    onClick={() => deleteProduct(p.id)}
                                                    className="text-taupe hover:text-rose p-2 inline-block"
                                                    data-testid={`delete-product-${p.id}`}
                                                    aria-label="Eliminar"
                                                >
                                                    <Trash2 strokeWidth={1.5} className="w-4 h-4" />
                                                </button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                )}

                {tab === "orders" && (
                    <div data-testid="orders-panel">
                        <h2 className="font-serif text-3xl text-charcoal mb-6">Pedidos</h2>
                        {orders.length === 0 ? (
                            <p className="text-taupe italic font-serif text-xl">Aún no hay pedidos.</p>
                        ) : (
                            <div className="space-y-4">
                                {orders.map((o) => (
                                    <div
                                        key={o.id}
                                        className="bg-white rounded-md border border-border-soft p-5"
                                        data-testid={`order-row-${o.id}`}
                                    >
                                        <div className="flex flex-wrap justify-between gap-4">
                                            <div>
                                                <p className="overline">Pedido #{o.id.slice(0, 8).toUpperCase()}{!o.seen_by_admin && <span className="ml-2 inline-block bg-red-500 text-white text-[9px] px-2 py-0.5 rounded-full uppercase tracking-wider">Nuevo</span>}</p>
                                                <p className="font-serif text-xl mt-1">{o.customer_name}</p>
                                                <p className="text-sm text-taupe">
                                                    {o.customer_email} · {o.customer_phone}
                                                </p>
                                                <p className="text-sm text-taupe">{o.address}, {o.city}</p>
                                                <p className="text-xs text-taupe mt-1">
                                                    {new Date(o.created_at).toLocaleString("es-ES")}{o.payment_method && <span className="ml-2 text-gold">· {o.payment_method}</span>}
                                                </p>
                                            </div>
                                            <div className="text-right">
                                                <p className="font-serif text-2xl text-rose">${o.total.toFixed(2)}</p>
                                                <select
                                                    value={o.status}
                                                    onChange={(e) => updateOrderStatus(o.id, e.target.value)}
                                                    className="mt-2 bg-blush border border-border-soft rounded px-3 py-1.5 text-xs uppercase tracking-wider"
                                                    data-testid={`order-status-${o.id}`}
                                                >
                                                    <option value="pendiente">Pendiente</option>
                                                    <option value="enviado">Enviado</option>
                                                    <option value="entregado">Entregado</option>
                                                    <option value="cancelado">Cancelado</option>
                                                </select>
                                            </div>
                                        </div>
                                        <div className="mt-4 pt-4 border-t border-border-soft space-y-1 text-sm">
                                            {o.items.map((it, i) => (
                                                <div key={i} className="flex justify-between text-taupe">
                                                    <span>{it.quantity} × {it.name}</span>
                                                    <span>${(it.quantity * it.price).toFixed(2)}</span>
                                                </div>
                                            ))}
                                            {o.notes && (
                                                <p className="text-xs italic text-taupe pt-2">&ldquo;{o.notes}&rdquo;</p>
                                            )}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                )}

                {tab === "subscribers" && (
                    <div data-testid="subscribers-panel">
                        <h2 className="font-serif text-3xl text-charcoal mb-6">Suscriptoras</h2>
                        {subscribers.length === 0 ? (
                            <p className="text-taupe italic font-serif text-xl">
                                Aún no hay suscriptoras. Aparecerán aquí cuando se registren con el popup de bienvenida.
                            </p>
                        ) : (
                            <div className="bg-white rounded-md border border-border-soft overflow-hidden">
                                <table className="w-full text-sm">
                                    <thead className="bg-blush text-taupe text-xs uppercase tracking-wider">
                                        <tr>
                                            <th className="text-left px-4 py-3">Email</th>
                                            <th className="text-left px-4 py-3">Cupón</th>
                                            <th className="text-left px-4 py-3">Fecha</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {subscribers.map((s) => (
                                            <tr key={s.id} className="border-t border-border-soft" data-testid={`subscriber-row-${s.id}`}>
                                                <td className="px-4 py-3">{s.email}</td>
                                                <td className="px-4 py-3 font-mono text-xs text-rose">{s.coupon_code}</td>
                                                <td className="px-4 py-3 text-taupe text-xs">
                                                    {new Date(s.created_at).toLocaleString("es-ES")}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </div>
                )}

                {tab === "categories" && (
                    <CategoriesPanel categories={categories} onChange={reloadCategories} />
                )}

                {tab === "settings" && (
                    <SettingsPanel settings={settings} onSaved={reloadSettings} />
                )}

                {tab === "slides" && (
                    <SlidesPanel slides={slides} onChange={loadSlides} />
                )}

                {tab === "shipping" && (
                    <ShippingPanel zones={zones} onChange={loadZones} />
                )}

                {tab === "promotions" && (
                    <PromotionsAdminPanel promotions={promotions} onChange={loadPromotions} />
                )}

            </div>

            {editing && (
                <ProductFormModal
                    product={editing === "new" ? null : editing}
                    categories={categories}
                    onClose={() => setEditing(null)}
                    onSaved={() => {
                        setEditing(null);
                        loadProducts();
                    }}
                />
            )}
        </div>
    );
}

function ProductFormModal({ product, categories, onClose, onSaved }) {
    const defaultCat = categories?.[0]?.slug || "";
    const [form, setForm] = useState(
        product || {
            name: "",
            description: "",
            price: 0,
            category: defaultCat,
            images: [""],
            stock: 0,
            featured: false,
        }
    );
    const [saving, setSaving] = useState(false);

    function handle(field, value) {
        setForm((f) => ({ ...f, [field]: value }));
    }
    function handleImage(i, value) {
        setForm((f) => {
            const imgs = [...(f.images || [])];
            imgs[i] = value;
            return { ...f, images: imgs };
        });
    }
    function addImage() {
        setForm((f) => ({ ...f, images: [...(f.images || []), ""] }));
    }
    function removeImage(i) {
        setForm((f) => ({ ...f, images: f.images.filter((_, idx) => idx !== i) }));
    }

    async function handleSubmit(e) {
        e.preventDefault();
        setSaving(true);
        try {
            const payload = {
                name: form.name,
                description: form.description,
                price: parseFloat(form.price),
                category: form.category,
                images: (form.images || []).filter(Boolean),
                stock: parseInt(form.stock, 10) || 0,
                featured: !!form.featured,
            };
            if (product?.id) {
                await api.put(`/products/${product.id}`, payload);
                toast.success("Producto actualizado");
            } else {
                await api.post("/products", payload);
                toast.success("Producto creado");
            }
            onSaved();
        } catch (err) {
            toast.error(formatApiErrorDetail(err.response?.data?.detail));
        } finally {
            setSaving(false);
        }
    }

    return (
        <div
            className="fixed inset-0 z-50 bg-charcoal/40 backdrop-blur-sm flex items-center justify-center p-4 overflow-y-auto"
            onClick={onClose}
            data-testid="product-modal"
        >
            <form
                onClick={(e) => e.stopPropagation()}
                onSubmit={handleSubmit}
                className="bg-white rounded-md border border-border-soft w-full max-w-2xl p-8 my-8"
            >
                <h3 className="font-serif text-3xl text-charcoal mb-6">
                    {product ? "Editar producto" : "Nuevo producto"}
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="md:col-span-2">
                        <label className="overline block mb-1">Nombre</label>
                        <input
                            type="text"
                            value={form.name}
                            onChange={(e) => handle("name", e.target.value)}
                            required
                            className="w-full border border-border-soft rounded px-3 py-2 text-sm focus:ring-2 focus:ring-rose focus:outline-none"
                            data-testid="product-form-name"
                        />
                    </div>
                    <div className="md:col-span-2">
                        <label className="overline block mb-1">Descripción</label>
                        <textarea
                            value={form.description}
                            onChange={(e) => handle("description", e.target.value)}
                            rows={3}
                            className="w-full border border-border-soft rounded px-3 py-2 text-sm focus:ring-2 focus:ring-rose focus:outline-none"
                            data-testid="product-form-description"
                        />
                    </div>
                    <div>
                        <label className="overline block mb-1">Precio</label>
                        <input
                            type="number"
                            step="0.01"
                            value={form.price}
                            onChange={(e) => handle("price", e.target.value)}
                            required
                            className="w-full border border-border-soft rounded px-3 py-2 text-sm focus:ring-2 focus:ring-rose focus:outline-none"
                            data-testid="product-form-price"
                        />
                    </div>
                    <div>
                        <label className="overline block mb-1">Stock</label>
                        <input
                            type="number"
                            value={form.stock}
                            onChange={(e) => handle("stock", e.target.value)}
                            className="w-full border border-border-soft rounded px-3 py-2 text-sm focus:ring-2 focus:ring-rose focus:outline-none"
                            data-testid="product-form-stock"
                        />
                    </div>
                    <div>
                        <label className="overline block mb-1">Categoría</label>
                        <select
                            value={form.category}
                            onChange={(e) => handle("category", e.target.value)}
                            className="w-full border border-border-soft rounded px-3 py-2 text-sm focus:ring-2 focus:ring-rose focus:outline-none"
                            data-testid="product-form-category"
                        >
                            {categories?.map((c) => (
                                <option key={c.slug} value={c.slug}>
                                    {c.name}
                                </option>
                            ))}
                        </select>
                    </div>
                    <div className="flex items-center gap-2 pt-7">
                        <input
                            type="checkbox"
                            id="featured"
                            checked={!!form.featured}
                            onChange={(e) => handle("featured", e.target.checked)}
                            className="w-4 h-4 accent-rose"
                            data-testid="product-form-featured"
                        />
                        <label htmlFor="featured" className="text-sm">Destacar en inicio</label>
                    </div>
                    <div className="md:col-span-2">
                        <label className="overline block mb-2">Imágenes del producto</label>
                        <div className="space-y-3">
                            {(form.images || []).filter(Boolean).length > 0 && (
                                <div className="grid grid-cols-4 gap-2">
                                    {(form.images || []).map((img, i) =>
                                        img ? (
                                            <div key={i} className="relative aspect-square rounded-lg overflow-hidden bg-blush border border-border-soft group">
                                                <img src={resolveImageUrl(img)} alt="" className="w-full h-full object-cover" />
                                                <button
                                                    type="button"
                                                    onClick={() => removeImage(i)}
                                                    className="absolute top-1 right-1 bg-white/90 hover:bg-rose hover:text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition"
                                                    data-testid={`remove-image-${i}`}
                                                    aria-label="Eliminar imagen"
                                                >
                                                    <Trash2 strokeWidth={1.5} className="w-3 h-3" />
                                                </button>
                                            </div>
                                        ) : null
                                    )}
                                </div>
                            )}

                            <ImageUploader
                                onUploaded={(url) => {
                                    setForm((f) => {
                                        const imgs = (f.images || []).filter(Boolean);
                                        return { ...f, images: [...imgs, url] };
                                    });
                                }}
                            />

                            <details className="text-xs">
                                <summary className="text-taupe hover:text-rose cursor-pointer tracking-wider uppercase">
                                    O añadir desde URL
                                </summary>
                                <div className="mt-2 space-y-2">
                                    {(form.images || []).map((img, i) => (
                                        <input
                                            key={i}
                                            type="text"
                                            value={img}
                                            onChange={(e) => handleImage(i, e.target.value)}
                                            placeholder="https://..."
                                            className="w-full border border-border-soft rounded px-3 py-2 text-sm focus:ring-2 focus:ring-rose focus:outline-none"
                                            data-testid={`product-form-image-${i}`}
                                        />
                                    ))}
                                    <button
                                        type="button"
                                        onClick={addImage}
                                        className="text-xs text-rose hover:text-rose-dark tracking-wider uppercase inline-flex items-center gap-1"
                                        data-testid="add-image-btn"
                                    >
                                        <Plus strokeWidth={1.5} className="w-3 h-3" /> Añadir campo URL
                                    </button>
                                </div>
                            </details>
                        </div>
                    </div>
                </div>
                <div className="mt-8 flex justify-end gap-3">
                    <button
                        type="button"
                        onClick={onClose}
                        className="px-6 py-2.5 text-xs tracking-[0.2em] uppercase text-taupe hover:text-charcoal"
                        data-testid="product-form-cancel"
                    >
                        Cancelar
                    </button>
                    <button
                        type="submit"
                        disabled={saving}
                        className="bg-rose text-white hover:bg-rose-dark rounded-md px-6 py-2.5 text-xs tracking-[0.2em] uppercase disabled:opacity-50"
                        data-testid="product-form-save"
                    >
                        {saving ? "Guardando..." : "Guardar"}
                    </button>
                </div>
            </form>
        </div>
    );
}


function ImageUploader({ onUploaded }) {
    const [uploading, setUploading] = useState(false);

    async function handleFile(e) {
        const file = e.target.files?.[0];
        if (!file) return;
        if (file.size > 8 * 1024 * 1024) {
            toast.error("Imagen demasiado grande (máx 8MB)");
            return;
        }
        setUploading(true);
        try {
            const fd = new FormData();
            fd.append("file", file);
            const { data } = await api.post("/admin/upload", fd, {
                headers: { "Content-Type": "multipart/form-data" },
            });
            onUploaded(data.url);
            toast.success("Imagen subida");
        } catch (err) {
            toast.error(formatApiErrorDetail(err.response?.data?.detail));
        } finally {
            setUploading(false);
            e.target.value = "";
        }
    }

    return (
        <label className="block border-2 border-dashed border-border-soft hover:border-rose rounded-lg p-6 text-center cursor-pointer transition" data-testid="image-uploader">
            <input
                type="file"
                accept="image/jpeg,image/png,image/gif,image/webp"
                onChange={handleFile}
                disabled={uploading}
                className="hidden"
                data-testid="image-file-input"
            />
            <Upload strokeWidth={1.5} className="w-6 h-6 mx-auto text-rose" />
            <p className="mt-2 text-sm text-charcoal">
                {uploading ? "Subiendo..." : "Sube tu foto"}
            </p>
            <p className="text-xs text-taupe mt-1">JPG, PNG, GIF o WEBP · máx 8MB</p>
        </label>
    );
}


function CategoriesPanel({ categories, onChange }) {
    const [editing, setEditing] = useState(null); // null | "new" | category

    async function handleDelete(c) {
        if (!window.confirm(`¿Eliminar la categoría "${c.name}"?`)) return;
        try {
            await api.delete(`/categories/${c.id}`);
            toast.success("Categoría eliminada");
            onChange();
        } catch (err) {
            toast.error(formatApiErrorDetail(err.response?.data?.detail));
        }
    }

    return (
        <div data-testid="categories-panel">
            <div className="flex justify-between items-center mb-6">
                <h2 className="font-serif text-3xl text-charcoal">Categorías</h2>
                <button
                    onClick={() => setEditing("new")}
                    className="bg-rose text-white hover:bg-rose-dark rounded-md px-5 py-2.5 transition text-xs tracking-[0.2em] uppercase inline-flex items-center gap-2"
                    data-testid="add-category-btn"
                >
                    <Plus strokeWidth={1.5} className="w-4 h-4" /> Nueva categoría
                </button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {categories.map((c) => (
                    <div
                        key={c.id}
                        className="bg-white rounded-md border border-border-soft overflow-hidden"
                        data-testid={`category-row-${c.id}`}
                    >
                        <div className="aspect-[5/3] bg-blush">
                            {c.image_url ? (
                                <img src={resolveImageUrl(c.image_url)} alt={c.name} className="w-full h-full object-cover" />
                            ) : (
                                <div className="w-full h-full flex items-center justify-center text-taupe text-xs">
                                    Sin imagen
                                </div>
                            )}
                        </div>
                        <div className="p-4 flex justify-between items-center">
                            <div>
                                <h3 className="font-serif text-xl text-charcoal">{c.name}</h3>
                                <p className="text-xs text-taupe font-mono">{c.slug} · orden {c.order}</p>
                            </div>
                            <div className="flex gap-1">
                                <button
                                    onClick={() => setEditing(c)}
                                    className="text-taupe hover:text-rose p-2"
                                    data-testid={`edit-category-${c.id}`}
                                    aria-label="Editar"
                                >
                                    <Pencil strokeWidth={1.5} className="w-4 h-4" />
                                </button>
                                <button
                                    onClick={() => handleDelete(c)}
                                    className="text-taupe hover:text-rose p-2"
                                    data-testid={`delete-category-${c.id}`}
                                    aria-label="Eliminar"
                                >
                                    <Trash2 strokeWidth={1.5} className="w-4 h-4" />
                                </button>
                            </div>
                        </div>
                    </div>
                ))}
            </div>
            {editing && (
                <CategoryFormModal
                    category={editing === "new" ? null : editing}
                    onClose={() => setEditing(null)}
                    onSaved={() => {
                        setEditing(null);
                        onChange();
                    }}
                />
            )}
        </div>
    );
}

function CategoryFormModal({ category, onClose, onSaved }) {
    const [form, setForm] = useState(
        category || { slug: "", name: "", image_url: "", order: 0 }
    );
    const [saving, setSaving] = useState(false);

    async function handleSubmit(e) {
        e.preventDefault();
        setSaving(true);
        try {
            const payload = {
                slug: form.slug,
                name: form.name,
                image_url: form.image_url || "",
                order: parseInt(form.order, 10) || 0,
            };
            if (category?.id) {
                await api.put(`/categories/${category.id}`, payload);
                toast.success("Categoría actualizada");
            } else {
                await api.post("/categories", payload);
                toast.success("Categoría creada");
            }
            onSaved();
        } catch (err) {
            toast.error(formatApiErrorDetail(err.response?.data?.detail));
        } finally {
            setSaving(false);
        }
    }

    function autoSlug(name) {
        return name
            .toLowerCase()
            .normalize("NFD")
            .replace(/[\u0300-\u036f]/g, "")
            .replace(/[^a-z0-9]+/g, "-")
            .replace(/^-+|-+$/g, "");
    }

    return (
        <div
            className="fixed inset-0 z-50 bg-charcoal/40 backdrop-blur-sm flex items-center justify-center p-4 overflow-y-auto"
            onClick={onClose}
            data-testid="category-modal"
        >
            <form
                onClick={(e) => e.stopPropagation()}
                onSubmit={handleSubmit}
                className="bg-white rounded-md border border-border-soft w-full max-w-lg p-8 my-8"
            >
                <h3 className="font-serif text-3xl text-charcoal mb-6">
                    {category ? "Editar categoría" : "Nueva categoría"}
                </h3>
                <div className="space-y-4">
                    <div>
                        <label className="overline block mb-1">Nombre</label>
                        <input
                            type="text"
                            value={form.name}
                            onChange={(e) => {
                                const name = e.target.value;
                                setForm((f) => ({
                                    ...f,
                                    name,
                                    slug: f.slug || category ? f.slug : autoSlug(name),
                                }));
                            }}
                            required
                            placeholder="Ej. Relojes"
                            className="w-full border border-border-soft rounded px-3 py-2 text-sm focus:ring-2 focus:ring-rose focus:outline-none"
                            data-testid="category-form-name"
                        />
                    </div>
                    <div>
                        <label className="overline block mb-1">Slug (URL)</label>
                        <input
                            type="text"
                            value={form.slug}
                            onChange={(e) => setForm((f) => ({ ...f, slug: autoSlug(e.target.value) }))}
                            required
                            placeholder="ej. relojes"
                            className="w-full border border-border-soft rounded px-3 py-2 text-sm font-mono focus:ring-2 focus:ring-rose focus:outline-none"
                            data-testid="category-form-slug"
                        />
                        <p className="text-[11px] text-taupe mt-1">
                            Aparece en la URL: /catalogo?cat={form.slug || "ejemplo"}
                        </p>
                    </div>
                    <div>
                        <label className="overline block mb-1">Imagen</label>
                        <ImageUploader onUploaded={(url) => setForm((f) => ({ ...f, image_url: url }))} />
                        {form.image_url && (
                            <div className="mt-2 flex items-center gap-3">
                                <img src={resolveImageUrl(form.image_url)} alt="" className="w-20 h-20 object-cover rounded" />
                                <button
                                    type="button"
                                    onClick={() => setForm((f) => ({ ...f, image_url: "" }))}
                                    className="text-xs text-taupe hover:text-rose tracking-wider uppercase"
                                >
                                    Quitar imagen
                                </button>
                            </div>
                        )}
                        <details className="mt-2 text-xs">
                            <summary className="text-taupe hover:text-rose cursor-pointer">O pegar URL</summary>
                            <input
                                type="text"
                                value={form.image_url}
                                onChange={(e) => setForm((f) => ({ ...f, image_url: e.target.value }))}
                                placeholder="https://..."
                                className="mt-2 w-full border border-border-soft rounded px-3 py-2 text-sm focus:ring-2 focus:ring-rose focus:outline-none"
                                data-testid="category-form-image-url"
                            />
                        </details>
                    </div>
                    <div>
                        <label className="overline block mb-1">Orden</label>
                        <input
                            type="number"
                            value={form.order}
                            onChange={(e) => setForm((f) => ({ ...f, order: e.target.value }))}
                            className="w-32 border border-border-soft rounded px-3 py-2 text-sm focus:ring-2 focus:ring-rose focus:outline-none"
                            data-testid="category-form-order"
                        />
                        <p className="text-[11px] text-taupe mt-1">Menor número aparece primero.</p>
                    </div>
                </div>
                <div className="mt-8 flex justify-end gap-3">
                    <button
                        type="button"
                        onClick={onClose}
                        className="px-6 py-2.5 text-xs tracking-[0.2em] uppercase text-taupe hover:text-charcoal"
                        data-testid="category-form-cancel"
                    >
                        Cancelar
                    </button>
                    <button
                        type="submit"
                        disabled={saving}
                        className="bg-rose text-white hover:bg-rose-dark rounded-md px-6 py-2.5 text-xs tracking-[0.2em] uppercase disabled:opacity-50"
                        data-testid="category-form-save"
                    >
                        {saving ? "Guardando..." : "Guardar"}
                    </button>
                </div>
            </form>
        </div>
    );
}

function SettingsPanel({ settings, onSaved }) {
    const [form, setForm] = useState(settings);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        setForm(settings);
    }, [settings]);

    function field(key, value) {
        setForm((f) => ({ ...f, [key]: value }));
    }

    async function handleSubmit(e) {
        e.preventDefault();
        setSaving(true);
        try {
            const payload = {
                ...form,
                free_shipping_threshold: parseFloat(form.free_shipping_threshold) || 0,
                whatsapp_number: (form.whatsapp_number || "").replace(/[^0-9]/g, ""),
                instagram: (form.instagram || "").replace(/^@/, ""),
            };
            await api.put("/settings", payload);
            toast.success("Configuración guardada");
            onSaved();
        } catch (err) {
            toast.error(formatApiErrorDetail(err.response?.data?.detail));
        } finally {
            setSaving(false);
        }
    }

    return (
        <div data-testid="settings-panel">
            <h2 className="font-serif text-3xl text-charcoal mb-6">Configuración</h2>
            <form onSubmit={handleSubmit} className="bg-white rounded-md border border-border-soft p-6 max-w-3xl">
                <p className="overline mb-4">Portada (inicio)</p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
                    <div>
                        <label className="overline block mb-2">Imagen principal (grande)</label>
                        {form.hero_main_image && (
                            <div className="relative aspect-[4/5] rounded-lg overflow-hidden bg-blush border border-border-soft mb-2 max-w-[180px]">
                                <img src={resolveImageUrl(form.hero_main_image)} alt="" className="w-full h-full object-cover" />
                                <button
                                    type="button"
                                    onClick={() => field("hero_main_image", "")}
                                    className="absolute top-1 right-1 bg-white/90 hover:bg-rose hover:text-white rounded-full p-1 transition"
                                    aria-label="Quitar"
                                    data-testid="remove-hero-main"
                                >
                                    <Trash2 strokeWidth={1.5} className="w-3 h-3" />
                                </button>
                            </div>
                        )}
                        <ImageUploader onUploaded={(url) => field("hero_main_image", url)} />
                    </div>
                    <div>
                        <label className="overline block mb-2">Imagen secundaria (chica)</label>
                        {form.hero_secondary_image && (
                            <div className="relative aspect-square rounded-lg overflow-hidden bg-blush border border-border-soft mb-2 max-w-[140px]">
                                <img src={resolveImageUrl(form.hero_secondary_image)} alt="" className="w-full h-full object-cover" />
                                <button
                                    type="button"
                                    onClick={() => field("hero_secondary_image", "")}
                                    className="absolute top-1 right-1 bg-white/90 hover:bg-rose hover:text-white rounded-full p-1 transition"
                                    aria-label="Quitar"
                                    data-testid="remove-hero-secondary"
                                >
                                    <Trash2 strokeWidth={1.5} className="w-3 h-3" />
                                </button>
                            </div>
                        )}
                        <ImageUploader onUploaded={(url) => field("hero_secondary_image", url)} />
                    </div>
                    <div>
                        <label className="overline block mb-1">Texto superior (overline)</label>
                        <input
                            type="text"
                            value={form.hero_overline || ""}
                            onChange={(e) => field("hero_overline", e.target.value)}
                            className="w-full border border-border-soft rounded px-3 py-2 text-sm focus:ring-2 focus:ring-rose focus:outline-none"
                            data-testid="settings-hero-overline"
                        />
                    </div>
                    <div>
                        <label className="overline block mb-1">Título principal</label>
                        <input
                            type="text"
                            value={form.hero_title || ""}
                            onChange={(e) => field("hero_title", e.target.value)}
                            className="w-full border border-border-soft rounded px-3 py-2 text-sm focus:ring-2 focus:ring-rose focus:outline-none"
                            data-testid="settings-hero-title"
                        />
                    </div>
                    <div className="md:col-span-2">
                        <label className="overline block mb-1">Subtítulo</label>
                        <textarea
                            value={form.hero_subtitle || ""}
                            onChange={(e) => field("hero_subtitle", e.target.value)}
                            rows={2}
                            className="w-full border border-border-soft rounded px-3 py-2 text-sm focus:ring-2 focus:ring-rose focus:outline-none"
                            data-testid="settings-hero-subtitle"
                        />
                    </div>
                </div>

                <p className="overline mb-4">Marca</p>
                <div className="mb-6">
                    <label className="overline block mb-1">Lema (frase corta del footer)</label>
                    <input
                        type="text"
                        value={form.brand_tagline || ""}
                        onChange={(e) => field("brand_tagline", e.target.value)}
                        className="w-full border border-border-soft rounded px-3 py-2 text-sm focus:ring-2 focus:ring-rose focus:outline-none"
                        data-testid="settings-tagline"
                    />
                </div>

                <p className="overline mb-4">Contacto</p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                    <div>
                        <label className="overline block mb-1">Email</label>
                        <input
                            type="email"
                            value={form.contact_email || ""}
                            onChange={(e) => field("contact_email", e.target.value)}
                            className="w-full border border-border-soft rounded px-3 py-2 text-sm focus:ring-2 focus:ring-rose focus:outline-none"
                            data-testid="settings-email"
                        />
                    </div>
                    <div>
                        <label className="overline block mb-1">Instagram (sin @)</label>
                        <input
                            type="text"
                            value={form.instagram || ""}
                            onChange={(e) => field("instagram", e.target.value)}
                            placeholder="tuusuario"
                            className="w-full border border-border-soft rounded px-3 py-2 text-sm focus:ring-2 focus:ring-rose focus:outline-none"
                            data-testid="settings-instagram"
                        />
                    </div>
                    <div>
                        <label className="overline block mb-1">WhatsApp (solo números con código país)</label>
                        <input
                            type="text"
                            value={form.whatsapp_number || ""}
                            onChange={(e) => field("whatsapp_number", e.target.value)}
                            placeholder="573001234567"
                            className="w-full border border-border-soft rounded px-3 py-2 text-sm focus:ring-2 focus:ring-rose focus:outline-none"
                            data-testid="settings-whatsapp"
                        />
                        <p className="text-[11px] text-taupe mt-1">Deja vacío para ocultar el botón flotante.</p>
                    </div>
                    <div>
                        <label className="overline block mb-1">Mensaje predefinido WhatsApp</label>
                        <input
                            type="text"
                            value={form.whatsapp_message || ""}
                            onChange={(e) => field("whatsapp_message", e.target.value)}
                            className="w-full border border-border-soft rounded px-3 py-2 text-sm focus:ring-2 focus:ring-rose focus:outline-none"
                            data-testid="settings-whatsapp-msg"
                        />
                    </div>
                    <div>
                        <label className="overline block mb-1">Dirección</label>
                        <input
                            type="text"
                            value={form.address || ""}
                            onChange={(e) => field("address", e.target.value)}
                            className="w-full border border-border-soft rounded px-3 py-2 text-sm focus:ring-2 focus:ring-rose focus:outline-none"
                            data-testid="settings-address"
                        />
                    </div>
                    <div>
                        <label className="overline block mb-1">Ciudad</label>
                        <input
                            type="text"
                            value={form.city || ""}
                            onChange={(e) => field("city", e.target.value)}
                            className="w-full border border-border-soft rounded px-3 py-2 text-sm focus:ring-2 focus:ring-rose focus:outline-none"
                            data-testid="settings-city"
                        />
                    </div>
                </div>

                <p className="overline mb-4">Tienda</p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                        <label className="overline block mb-1">Umbral envío gratis ($)</label>
                        <input
                            type="number"
                            step="0.01"
                            value={form.free_shipping_threshold ?? 0}
                            onChange={(e) => field("free_shipping_threshold", e.target.value)}
                            className="w-full border border-border-soft rounded px-3 py-2 text-sm focus:ring-2 focus:ring-rose focus:outline-none"
                            data-testid="settings-free-shipping"
                        />
                    </div>
                    <div>
                        <label className="overline block mb-1">Cupón de bienvenida</label>
                        <input
                            type="text"
                            value={form.welcome_coupon || ""}
                            onChange={(e) => field("welcome_coupon", e.target.value.toUpperCase())}
                            className="w-full border border-border-soft rounded px-3 py-2 text-sm font-mono focus:ring-2 focus:ring-rose focus:outline-none"
                            data-testid="settings-coupon"
                        />
                    </div>
                </div>

                <div className="mt-8 flex justify-end">
                    <button
                        type="submit"
                        disabled={saving}
                        className="bg-rose text-white hover:bg-rose-dark rounded-md px-6 py-2.5 text-xs tracking-[0.2em] uppercase disabled:opacity-50"
                        data-testid="settings-save"
                    >
                        {saving ? "Guardando..." : "Guardar configuración"}
                    </button>
                </div>
            </form>
        </div>
    );
}


function SlidesPanel({ slides, onChange }) {
    const [editing, setEditing] = useState(null);

    async function handleDelete(s) {
        if (!window.confirm(`¿Eliminar la slide "${s.title}"?`)) return;
        try {
            await api.delete(`/slides/${s.id}`);
            toast.success("Slide eliminada");
            onChange();
        } catch (err) {
            toast.error(formatApiErrorDetail(err.response?.data?.detail));
        }
    }

    return (
        <div data-testid="slides-panel">
            <div className="flex justify-between items-center mb-6">
                <h2 className="font-serif text-3xl text-charcoal">Carrusel de portada</h2>
                <button
                    onClick={() => setEditing("new")}
                    className="bg-gold text-white hover:bg-gold-dark rounded-md px-5 py-2.5 transition text-xs tracking-[0.2em] uppercase inline-flex items-center gap-2"
                    data-testid="add-slide-btn"
                >
                    <Plus strokeWidth={1.5} className="w-4 h-4" /> Nueva slide
                </button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {slides.map((s) => (
                    <div
                        key={s.id}
                        className="bg-white rounded-md border border-border-soft overflow-hidden"
                        data-testid={`slide-row-${s.id}`}
                    >
                        <div className="aspect-[16/9] bg-blush relative">
                            {s.image ? (
                                <img src={resolveImageUrl(s.image)} alt={s.title} className="w-full h-full object-cover" />
                            ) : (
                                <div className="w-full h-full flex items-center justify-center text-taupe text-xs">Sin imagen</div>
                            )}
                            <div className="absolute inset-0 bg-charcoal/30 flex flex-col justify-end p-3">
                                <p className="font-serif text-lg text-white leading-tight">{s.title}</p>
                                <p className="text-[11px] text-white/80">{s.subtitle}</p>
                            </div>
                            {!s.active && (
                                <span className="absolute top-2 left-2 text-[10px] bg-charcoal/70 text-white px-2 py-0.5 rounded-full uppercase tracking-wider">
                                    Oculta
                                </span>
                            )}
                        </div>
                        <div className="p-3 flex justify-between items-center">
                            <p className="text-xs text-taupe">orden {s.order} · {s.button_text || "sin botón"}</p>
                            <div className="flex gap-1">
                                <button onClick={() => setEditing(s)} className="text-taupe hover:text-gold p-2" data-testid={`edit-slide-${s.id}`} aria-label="Editar">
                                    <Pencil strokeWidth={1.5} className="w-4 h-4" />
                                </button>
                                <button onClick={() => handleDelete(s)} className="text-taupe hover:text-gold p-2" data-testid={`delete-slide-${s.id}`} aria-label="Eliminar">
                                    <Trash2 strokeWidth={1.5} className="w-4 h-4" />
                                </button>
                            </div>
                        </div>
                    </div>
                ))}
            </div>
            {editing && (
                <SlideFormModal
                    slide={editing === "new" ? null : editing}
                    onClose={() => setEditing(null)}
                    onSaved={() => {
                        setEditing(null);
                        onChange();
                    }}
                />
            )}
        </div>
    );
}

function SlideFormModal({ slide, onClose, onSaved }) {
    const [form, setForm] = useState(
        slide || { image: "", title: "", subtitle: "", button_text: "", button_link: "/catalogo", order: 0, active: true }
    );
    const [saving, setSaving] = useState(false);

    async function handleSubmit(e) {
        e.preventDefault();
        setSaving(true);
        try {
            const payload = { ...form, order: parseInt(form.order, 10) || 0 };
            if (slide?.id) await api.put(`/slides/${slide.id}`, payload);
            else await api.post("/slides", payload);
            toast.success("Slide guardada");
            onSaved();
        } catch (err) {
            toast.error(formatApiErrorDetail(err.response?.data?.detail));
        } finally {
            setSaving(false);
        }
    }

    function set(k, v) { setForm((f) => ({ ...f, [k]: v })); }

    return (
        <div className="fixed inset-0 z-50 bg-charcoal/40 backdrop-blur-sm flex items-center justify-center p-4 overflow-y-auto" onClick={onClose} data-testid="slide-modal">
            <form onClick={(e) => e.stopPropagation()} onSubmit={handleSubmit} className="bg-white rounded-md border border-border-soft w-full max-w-2xl p-8 my-8">
                <h3 className="font-serif text-3xl text-charcoal mb-6">{slide ? "Editar slide" : "Nueva slide"}</h3>
                <div className="space-y-4">
                    <div>
                        <label className="overline block mb-1">Imagen de fondo</label>
                        {form.image && (
                            <div className="relative aspect-[16/9] rounded-lg overflow-hidden bg-blush border border-border-soft mb-2 max-w-md">
                                <img src={resolveImageUrl(form.image)} alt="" className="w-full h-full object-cover" />
                                <button type="button" onClick={() => set("image", "")} className="absolute top-2 right-2 bg-white/90 hover:bg-rose hover:text-white rounded-full p-1.5 transition" aria-label="Quitar">
                                    <Trash2 strokeWidth={1.5} className="w-3.5 h-3.5" />
                                </button>
                            </div>
                        )}
                        <ImageUploader onUploaded={(url) => set("image", url)} />
                    </div>
                    <div>
                        <label className="overline block mb-1">Título</label>
                        <input type="text" value={form.title} onChange={(e) => set("title", e.target.value)} required placeholder="Ej. Nueva Colección" className="w-full border border-border-soft rounded px-3 py-2 text-sm focus:ring-2 focus:ring-gold focus:outline-none" data-testid="slide-form-title" />
                    </div>
                    <div>
                        <label className="overline block mb-1">Subtítulo</label>
                        <input type="text" value={form.subtitle} onChange={(e) => set("subtitle", e.target.value)} placeholder="Ej. Joyas hechas con amor" className="w-full border border-border-soft rounded px-3 py-2 text-sm focus:ring-2 focus:ring-gold focus:outline-none" data-testid="slide-form-subtitle" />
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                        <div>
                            <label className="overline block mb-1">Texto botón</label>
                            <input type="text" value={form.button_text} onChange={(e) => set("button_text", e.target.value)} placeholder="Ver colección" className="w-full border border-border-soft rounded px-3 py-2 text-sm focus:ring-2 focus:ring-gold focus:outline-none" data-testid="slide-form-button-text" />
                        </div>
                        <div>
                            <label className="overline block mb-1">Enlace botón</label>
                            <input type="text" value={form.button_link} onChange={(e) => set("button_link", e.target.value)} placeholder="/catalogo" className="w-full border border-border-soft rounded px-3 py-2 text-sm focus:ring-2 focus:ring-gold focus:outline-none" data-testid="slide-form-button-link" />
                        </div>
                    </div>
                    <div className="grid grid-cols-2 gap-3 items-end">
                        <div>
                            <label className="overline block mb-1">Orden</label>
                            <input type="number" value={form.order} onChange={(e) => set("order", e.target.value)} className="w-full border border-border-soft rounded px-3 py-2 text-sm focus:ring-2 focus:ring-gold focus:outline-none" data-testid="slide-form-order" />
                        </div>
                        <label className="inline-flex items-center gap-2 text-sm pb-2">
                            <input type="checkbox" checked={!!form.active} onChange={(e) => set("active", e.target.checked)} className="w-4 h-4 accent-rose" data-testid="slide-form-active" />
                            Visible en la web
                        </label>
                    </div>
                </div>
                <div className="mt-8 flex justify-end gap-3">
                    <button type="button" onClick={onClose} className="px-6 py-2.5 text-xs tracking-[0.2em] uppercase text-taupe hover:text-charcoal" data-testid="slide-form-cancel">Cancelar</button>
                    <button type="submit" disabled={saving} className="bg-gold text-white hover:bg-gold-dark rounded-md px-6 py-2.5 text-xs tracking-[0.2em] uppercase disabled:opacity-50" data-testid="slide-form-save">{saving ? "Guardando..." : "Guardar"}</button>
                </div>
            </form>
        </div>
    );
}

function ShippingPanel({ zones, onChange }) {
    const [editing, setEditing] = useState(null);

    async function handleDelete(z) {
        if (!window.confirm(`¿Eliminar la zona "${z.name}"?`)) return;
        try {
            await api.delete(`/shipping-zones/${z.id}`);
            toast.success("Zona eliminada");
            onChange();
        } catch (err) {
            toast.error(formatApiErrorDetail(err.response?.data?.detail));
        }
    }

    return (
        <div data-testid="shipping-panel">
            <div className="flex justify-between items-center mb-6">
                <h2 className="font-serif text-3xl text-charcoal">Zonas de envío</h2>
                <button onClick={() => setEditing("new")} className="bg-gold text-white hover:bg-gold-dark rounded-md px-5 py-2.5 transition text-xs tracking-[0.2em] uppercase inline-flex items-center gap-2" data-testid="add-zone-btn">
                    <Plus strokeWidth={1.5} className="w-4 h-4" /> Nueva zona
                </button>
            </div>
            <div className="bg-white rounded-md border border-border-soft overflow-hidden">
                <table className="w-full text-sm">
                    <thead className="bg-blush text-taupe text-xs uppercase tracking-wider">
                        <tr>
                            <th className="text-left px-4 py-3">Zona</th>
                            <th className="text-left px-4 py-3">Costo</th>
                            <th className="text-left px-4 py-3">Tiempo</th>
                            <th className="text-left px-4 py-3">Envío gratis</th>
                            <th className="text-right px-4 py-3">Acciones</th>
                        </tr>
                    </thead>
                    <tbody>
                        {zones.map((z) => (
                            <tr key={z.id} className="border-t border-border-soft" data-testid={`zone-row-${z.id}`}>
                                <td className="px-4 py-3 font-serif text-base">{z.name}</td>
                                <td className="px-4 py-3">${z.cost.toLocaleString("es-CO")}</td>
                                <td className="px-4 py-3 text-taupe">{z.delivery_time}</td>
                                <td className="px-4 py-3">{z.free_threshold > 0 ? `≥ $${z.free_threshold.toLocaleString("es-CO")}` : "—"}</td>
                                <td className="px-4 py-3 text-right">
                                    <button onClick={() => setEditing(z)} className="text-taupe hover:text-gold p-2 inline-block" data-testid={`edit-zone-${z.id}`}>
                                        <Pencil strokeWidth={1.5} className="w-4 h-4" />
                                    </button>
                                    <button onClick={() => handleDelete(z)} className="text-taupe hover:text-gold p-2 inline-block" data-testid={`delete-zone-${z.id}`}>
                                        <Trash2 strokeWidth={1.5} className="w-4 h-4" />
                                    </button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
            {editing && (
                <ZoneFormModal zone={editing === "new" ? null : editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); onChange(); }} />
            )}
        </div>
    );
}

function ZoneFormModal({ zone, onClose, onSaved }) {
    const [form, setForm] = useState(zone || { name: "", cost: 0, delivery_time: "", free_threshold: 0, order: 0 });
    const [saving, setSaving] = useState(false);

    async function handleSubmit(e) {
        e.preventDefault();
        setSaving(true);
        try {
            const payload = {
                name: form.name,
                cost: parseFloat(form.cost) || 0,
                delivery_time: form.delivery_time,
                free_threshold: parseFloat(form.free_threshold) || 0,
                order: parseInt(form.order, 10) || 0,
            };
            if (zone?.id) await api.put(`/shipping-zones/${zone.id}`, payload);
            else await api.post("/shipping-zones", payload);
            toast.success("Zona guardada");
            onSaved();
        } catch (err) {
            toast.error(formatApiErrorDetail(err.response?.data?.detail));
        } finally {
            setSaving(false);
        }
    }

    function set(k, v) { setForm((f) => ({ ...f, [k]: v })); }

    return (
        <div className="fixed inset-0 z-50 bg-charcoal/40 backdrop-blur-sm flex items-center justify-center p-4 overflow-y-auto" onClick={onClose} data-testid="zone-modal">
            <form onClick={(e) => e.stopPropagation()} onSubmit={handleSubmit} className="bg-white rounded-md border border-border-soft w-full max-w-lg p-8 my-8">
                <h3 className="font-serif text-3xl text-charcoal mb-6">{zone ? "Editar zona" : "Nueva zona"}</h3>
                <div className="space-y-4">
                    <div>
                        <label className="overline block mb-1">Nombre</label>
                        <input type="text" value={form.name} onChange={(e) => set("name", e.target.value)} required placeholder="Ej. Bogotá" className="w-full border border-border-soft rounded px-3 py-2 text-sm focus:ring-2 focus:ring-gold focus:outline-none" data-testid="zone-form-name" />
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                        <div>
                            <label className="overline block mb-1">Costo ($)</label>
                            <input type="number" step="100" value={form.cost} onChange={(e) => set("cost", e.target.value)} className="w-full border border-border-soft rounded px-3 py-2 text-sm focus:ring-2 focus:ring-gold focus:outline-none" data-testid="zone-form-cost" />
                        </div>
                        <div>
                            <label className="overline block mb-1">Envío gratis desde ($)</label>
                            <input type="number" step="1000" value={form.free_threshold} onChange={(e) => set("free_threshold", e.target.value)} className="w-full border border-border-soft rounded px-3 py-2 text-sm focus:ring-2 focus:ring-gold focus:outline-none" data-testid="zone-form-free" />
                        </div>
                    </div>
                    <div>
                        <label className="overline block mb-1">Tiempo de entrega</label>
                        <input type="text" value={form.delivery_time} onChange={(e) => set("delivery_time", e.target.value)} placeholder="Ej. 1-2 días hábiles" className="w-full border border-border-soft rounded px-3 py-2 text-sm focus:ring-2 focus:ring-gold focus:outline-none" data-testid="zone-form-time" />
                    </div>
                    <div>
                        <label className="overline block mb-1">Orden</label>
                        <input type="number" value={form.order} onChange={(e) => set("order", e.target.value)} className="w-32 border border-border-soft rounded px-3 py-2 text-sm focus:ring-2 focus:ring-gold focus:outline-none" data-testid="zone-form-order" />
                    </div>
                </div>
                <div className="mt-8 flex justify-end gap-3">
                    <button type="button" onClick={onClose} className="px-6 py-2.5 text-xs tracking-[0.2em] uppercase text-taupe hover:text-charcoal" data-testid="zone-form-cancel">Cancelar</button>
                    <button type="submit" disabled={saving} className="bg-gold text-white hover:bg-gold-dark rounded-md px-6 py-2.5 text-xs tracking-[0.2em] uppercase disabled:opacity-50" data-testid="zone-form-save">{saving ? "Guardando..." : "Guardar"}</button>
                </div>
            </form>
        </div>
    );
}

function PromotionsAdminPanel({ promotions, onChange }) {
    const [editing, setEditing] = useState(null);

    async function handleDelete(p) {
        if (!window.confirm(`¿Eliminar la promoción "${p.name}"?`)) return;
        try {
            await api.delete(`/promotions/${p.id}`);
            toast.success("Promoción eliminada");
            onChange();
        } catch (err) {
            toast.error(formatApiErrorDetail(err.response?.data?.detail));
        }
    }

    return (
        <div data-testid="promotions-admin-panel">
            <div className="flex justify-between items-center mb-6">
                <h2 className="font-serif text-3xl text-charcoal">Promociones</h2>
                <button onClick={() => setEditing("new")} className="bg-gold text-white hover:bg-gold-dark rounded-md px-5 py-2.5 transition text-xs tracking-[0.2em] uppercase inline-flex items-center gap-2" data-testid="add-promo-btn">
                    <Plus strokeWidth={1.5} className="w-4 h-4" /> Nueva promoción
                </button>
            </div>
            {promotions.length === 0 ? (
                <p className="text-taupe italic font-serif text-xl">Aún no hay promociones. Crea una para mostrarla en la home.</p>
            ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {promotions.map((p) => (
                        <div key={p.id} className="bg-white rounded-md border border-border-soft p-5" data-testid={`promo-row-${p.id}`}>
                            <div className="flex justify-between items-start gap-3">
                                <div>
                                    <p className="overline">{p.active ? "Activa" : "Inactiva"} · orden {p.order}</p>
                                    <h3 className="font-serif text-2xl text-charcoal mt-1">{p.name}</h3>
                                    {p.description && <p className="text-sm text-taupe mt-1">{p.description}</p>}
                                </div>
                                <div className="text-right">
                                    {p.discount_percent > 0 && <div className="font-serif text-3xl text-gold">{p.discount_percent}%</div>}
                                </div>
                            </div>
                            <div className="mt-3 text-xs text-taupe space-y-1">
                                {p.code && <p>Código: <span className="font-mono text-gold">{p.code}</span></p>}
                                {p.ends_at && <p>Vence: {new Date(p.ends_at).toLocaleDateString("es-ES")}</p>}
                            </div>
                            <div className="mt-3 flex justify-end gap-1">
                                <button onClick={() => setEditing(p)} className="text-taupe hover:text-gold p-2" data-testid={`edit-promo-${p.id}`}>
                                    <Pencil strokeWidth={1.5} className="w-4 h-4" />
                                </button>
                                <button onClick={() => handleDelete(p)} className="text-taupe hover:text-gold p-2" data-testid={`delete-promo-${p.id}`}>
                                    <Trash2 strokeWidth={1.5} className="w-4 h-4" />
                                </button>
                            </div>
                        </div>
                    ))}
                </div>
            )}
            {editing && (
                <PromoFormModal promo={editing === "new" ? null : editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); onChange(); }} />
            )}
        </div>
    );
}

function PromoFormModal({ promo, onClose, onSaved }) {
    const [form, setForm] = useState(promo || {
        name: "",
        description: "",
        discount_percent: 0,
        code: "",
        starts_at: "",
        ends_at: "",
        active: true,
        order: 0,
    });
    const [saving, setSaving] = useState(false);

    async function handleSubmit(e) {
        e.preventDefault();
        setSaving(true);
        try {
            const payload = {
                name: form.name,
                description: form.description || "",
                discount_percent: parseFloat(form.discount_percent) || 0,
                code: form.code || "",
                starts_at: form.starts_at || null,
                ends_at: form.ends_at || null,
                active: !!form.active,
                order: parseInt(form.order, 10) || 0,
            };
            if (promo?.id) await api.put(`/promotions/${promo.id}`, payload);
            else await api.post("/promotions", payload);
            toast.success("Promoción guardada");
            onSaved();
        } catch (err) {
            toast.error(formatApiErrorDetail(err.response?.data?.detail));
        } finally {
            setSaving(false);
        }
    }

    function set(k, v) { setForm((f) => ({ ...f, [k]: v })); }

    return (
        <div className="fixed inset-0 z-50 bg-charcoal/40 backdrop-blur-sm flex items-center justify-center p-4 overflow-y-auto" onClick={onClose} data-testid="promo-modal">
            <form onClick={(e) => e.stopPropagation()} onSubmit={handleSubmit} className="bg-white rounded-md border border-border-soft w-full max-w-2xl p-8 my-8">
                <h3 className="font-serif text-3xl text-charcoal mb-6">{promo ? "Editar promoción" : "Nueva promoción"}</h3>
                <div className="space-y-4">
                    <div>
                        <label className="overline block mb-1">Nombre</label>
                        <input type="text" value={form.name} onChange={(e) => set("name", e.target.value)} required placeholder="Ej. Black Friday" className="w-full border border-border-soft rounded px-3 py-2 text-sm focus:ring-2 focus:ring-gold focus:outline-none" data-testid="promo-form-name" />
                    </div>
                    <div>
                        <label className="overline block mb-1">Descripción</label>
                        <textarea rows={2} value={form.description} onChange={(e) => set("description", e.target.value)} placeholder="Ej. 30% off en toda la colección" className="w-full border border-border-soft rounded px-3 py-2 text-sm focus:ring-2 focus:ring-gold focus:outline-none" data-testid="promo-form-description" />
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                        <div>
                            <label className="overline block mb-1">Descuento (%)</label>
                            <input type="number" step="1" min="0" max="100" value={form.discount_percent} onChange={(e) => set("discount_percent", e.target.value)} className="w-full border border-border-soft rounded px-3 py-2 text-sm focus:ring-2 focus:ring-gold focus:outline-none" data-testid="promo-form-percent" />
                        </div>
                        <div>
                            <label className="overline block mb-1">Código (opcional)</label>
                            <input type="text" value={form.code} onChange={(e) => set("code", e.target.value.toUpperCase())} placeholder="BLACK30" className="w-full border border-border-soft rounded px-3 py-2 text-sm font-mono focus:ring-2 focus:ring-gold focus:outline-none" data-testid="promo-form-code" />
                        </div>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                        <div>
                            <label className="overline block mb-1">Inicio</label>
                            <input type="date" value={form.starts_at?.slice(0, 10) || ""} onChange={(e) => set("starts_at", e.target.value)} className="w-full border border-border-soft rounded px-3 py-2 text-sm focus:ring-2 focus:ring-gold focus:outline-none" data-testid="promo-form-start" />
                        </div>
                        <div>
                            <label className="overline block mb-1">Fin</label>
                            <input type="date" value={form.ends_at?.slice(0, 10) || ""} onChange={(e) => set("ends_at", e.target.value)} className="w-full border border-border-soft rounded px-3 py-2 text-sm focus:ring-2 focus:ring-gold focus:outline-none" data-testid="promo-form-end" />
                        </div>
                    </div>
                    <div className="grid grid-cols-2 gap-3 items-end">
                        <div>
                            <label className="overline block mb-1">Orden</label>
                            <input type="number" value={form.order} onChange={(e) => set("order", e.target.value)} className="w-full border border-border-soft rounded px-3 py-2 text-sm focus:ring-2 focus:ring-gold focus:outline-none" data-testid="promo-form-order" />
                        </div>
                        <label className="inline-flex items-center gap-2 text-sm pb-2">
                            <input type="checkbox" checked={!!form.active} onChange={(e) => set("active", e.target.checked)} className="w-4 h-4 accent-rose" data-testid="promo-form-active" />
                            Activa en la web
                        </label>
                    </div>
                </div>
                <div className="mt-8 flex justify-end gap-3">
                    <button type="button" onClick={onClose} className="px-6 py-2.5 text-xs tracking-[0.2em] uppercase text-taupe hover:text-charcoal" data-testid="promo-form-cancel">Cancelar</button>
                    <button type="submit" disabled={saving} className="bg-gold text-white hover:bg-gold-dark rounded-md px-6 py-2.5 text-xs tracking-[0.2em] uppercase disabled:opacity-50" data-testid="promo-form-save">{saving ? "Guardando..." : "Guardar"}</button>
                </div>
            </form>
        </div>
    );
}
